import * as React from 'react';
import './CustomDayCalendar.scss';
import ToolTip from '../ToolTip/ToolTip';
import { lightenDarkenColor, getContrastColor, isLightColor } from '../../shared/utils';
import classnames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import ViewColumnIcon from '@material-ui/icons/ViewColumn'
import ViewHeadlineIcon from '@material-ui/icons/ViewHeadline'
import SettingsIcon from '@material-ui/icons/Settings'
import { IconButton } from '@material-ui/core';
  
export interface CalendarColumn {
    name: string
    value: unknown
    lowlighted?: boolean
    highlighted?: boolean
    subtitle?: string
}

export interface CalendarSchedule {
    id: string,
    columnName: string,
    content: string | React.ReactNode,
    color?: string,
    startMinutesOfDay: number,
    endMinutesOfDay: number,
    toolTipContent?: React.ReactElement | string,
    opacity?: number,
    ignoreOverlap?: boolean,
    onClick?: (minutesOfDay: number, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void,
    onContextMenu?: (minutesOfDay: number, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void,
    onMouseOver?: (minutesOfDay: number, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void,
}

export interface CalendarLayoutConfiguration {
    gridGap?: number,
    timeColumnWidth?: number,
    columnsWidth?: number,
    headerHeight?: number,
    rowsDefaultHeight?: number,
    minBlockHeight?: number,
    fontSize?: number,
    defaultDrawMode?: 'block' | 'name-only',
}

export interface CustomDayCalendarProps {
    schedules: CalendarSchedule[],
    columns: CalendarColumn[],
    stepInMinutes: number,
    openingHourInMinutesOfDay: number,
    closingHourInMinutesOfDay: number,
    layout?: CalendarLayoutConfiguration,
    className?: string,
    style?: React.CSSProperties,
    mouseLabel?: string | ((minutesOfDay: number) => string),
    onClick?: (column: CalendarColumn, minutesOfDay: number, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void,
    onContextMenu?: (column: CalendarColumn, minutesOfDay: number, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void,
    onChangeColumnOrder?: (source: number, target: number) => void,
    onSettingsClick?: () => void,
}

const minVisualDurationMinutes = 5

export const blockStyle = (color?: string, opacity: number = 1): React.CSSProperties => ({
    color: getContrastColor(color ?? '#444444'),
    background: color ?? '#444444',
    // backgroundImage: `linear-gradient(
    //     250deg,
    //     ${color} 10%,
    //     ${lightenDarkenColor(color, 50)},
    //     ${color} 40%
    // )`,
    border: `1px solid ${lightenDarkenColor(color ?? '#444444', -80)}`,
    borderRadius: '5px',
    opacity: opacity,
})

export const nameOnlyStyle = (color?: string): React.CSSProperties => {
    const c = color || '#444444'
    return {
        color: isLightColor(c) ? lightenDarkenColor(c, -50) : c,
        background: 'inherit',
        fontWeight: 'bold',
    }
}

const getShortestDuration = (schedules: CalendarSchedule[]) => schedules.reduce((shortest, schedule) => {
    const durationMinutes = (schedule.endMinutesOfDay - schedule.startMinutesOfDay)
    return Math.max(durationMinutes < shortest ? durationMinutes : shortest, minVisualDurationMinutes)
}, 1000)

const CustomDayCalendar: React.FC<CustomDayCalendarProps> = (props) => {
  
    const {
        schedules,
        columns,
        stepInMinutes,
        openingHourInMinutesOfDay,
        closingHourInMinutesOfDay,
        className,
        style,
        mouseLabel,
        onChangeColumnOrder,
        onClick,
        onContextMenu: onContextMenuBackground,
        onSettingsClick,
    } = props;

    const {
        gridGap = 5,
        timeColumnWidth = 60,
        columnsWidth = 140,
        headerHeight = 40,
        rowsDefaultHeight = 30,
        minBlockHeight = 15,
        fontSize = 11,
        defaultDrawMode = 'block',
    } = props.layout || {};

    const nameOnlyHeight = 15;

    const containerRef = React.useRef<HTMLDivElement>(null)
    const outerContainerRef = React.useRef<HTMLDivElement>(null)

    const [drawMode, setDrawMode] = useState<'block'|'name-only'>(defaultDrawMode)
    useEffect(() => setDrawMode(defaultDrawMode), [defaultDrawMode])

    const [mousePos, setMousePos] = useState<{x: number, y: number, minutes: number} | null>(null)

    // Long-press support for mobile (mirrors right-click / contextmenu)
    const longPressTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const longPressStartRef = React.useRef<{ x: number; y: number } | null>(null);
    const longPressFiredRef = React.useRef(false);

    const clearLongPress = () => {
        if (longPressTimerRef.current != null) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        longPressStartRef.current = null;
    };

    const makeFakeMouseEvent = (clientX: number, clientY: number) =>
        ({ clientX, clientY, preventDefault: () => {}, stopPropagation: () => {} }) as unknown as React.MouseEvent<HTMLDivElement>;

    const onTouchStartBackground = (e: React.TouchEvent<HTMLDivElement>) => {
        if (!onContextMenuBackground) return;
        const touch = e.touches[0];
        const { clientX, clientY } = touch;
        longPressStartRef.current = { x: clientX, y: clientY };
        clearLongPress();
        longPressTimerRef.current = setTimeout(() => {
            longPressTimerRef.current = null;
            longPressFiredRef.current = true;
            const mins = getMinutesOfDay(clientY - (containerRef.current?.getBoundingClientRect().top ?? 0));
            const colIdx = Math.floor((clientX - (containerRef.current?.getBoundingClientRect().left ?? 0)) / (columnsWidth + gridGap));
            if (mins < openingHourInMinutesOfDay || mins > closingHourInMinutesOfDay || colIdx < 0 || colIdx >= columns.length) return;
            onContextMenuBackground(columns[colIdx], mins, makeFakeMouseEvent(clientX, clientY));
        }, 500);
    };

    const onTouchStartSchedule = (schedule: CalendarSchedule) => (e: React.TouchEvent<HTMLDivElement>) => {
        if (!schedule.onContextMenu) return;
        e.stopPropagation();
        const touch = e.touches[0];
        const { clientX, clientY } = touch;
        longPressStartRef.current = { x: clientX, y: clientY };
        clearLongPress();
        longPressTimerRef.current = setTimeout(() => {
            longPressTimerRef.current = null;
            longPressFiredRef.current = true;
            const mins = getMinutesOfDay(clientY - (containerRef.current?.getBoundingClientRect().top ?? 0));
            schedule.onContextMenu!(mins, makeFakeMouseEvent(clientX, clientY));
        }, 500);
    };

    const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if (!longPressStartRef.current) return;
        const t = e.touches[0];
        if (Math.abs(t.clientX - longPressStartRef.current.x) > 10 || Math.abs(t.clientY - longPressStartRef.current.y) > 10) {
            clearLongPress();
        }
    };

    const onTouchEnd = () => clearLongPress();

    const onMouseMoveContainer = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!mouseLabel) return
        const outerRect = outerContainerRef.current?.getBoundingClientRect()
        if (!outerRect) return
        const minutes = getMinutesOfDay(e.clientY - (containerRef.current?.getBoundingClientRect().top ?? 0))
        if (minutes < openingHourInMinutesOfDay || minutes > closingHourInMinutesOfDay) {
            setMousePos(null)
            return
        }
        setMousePos({ x: e.clientX - outerRect.left + 12, y: e.clientY - outerRect.top + 12, minutes })
    }

    const onMouseLeaveContainer = () => setMousePos(null)

    const nrTimeRows = Math.max(5, (closingHourInMinutesOfDay-openingHourInMinutesOfDay) / stepInMinutes + 1);
    const nrColumns = columns.length;

    const effectiveRowHeight = stepInMinutes >= 60
        ? rowsDefaultHeight
        : Math.max(16, Math.round(rowsDefaultHeight * 0.6));

    const schedulesInsideCell = React.useCallback((column: string, row: number) => schedules.filter(s => 
            s.columnName === column
            && s.startMinutesOfDay >= (openingHourInMinutesOfDay+row*stepInMinutes) 
            && s.startMinutesOfDay < (openingHourInMinutesOfDay+(row+1)*stepInMinutes))
        .sort((a ,b) => a.startMinutesOfDay - b.startMinutesOfDay), [schedules, openingHourInMinutesOfDay, stepInMinutes]);
    
    const getScheduleRow = React.useCallback(
        (time: number) => Math.max(0, Math.floor((time - openingHourInMinutesOfDay) / stepInMinutes)), 
        [openingHourInMinutesOfDay, stepInMinutes])

    const getTopPixelForTime = (time: number) => {
        const row = getScheduleRow(time);
        const remainingMinutesInRow = Math.floor(time - openingHourInMinutesOfDay) % stepInMinutes;
        return (row * gridGap)
            + rowsHeights.filter((_, i) => i < row).reduce((prev, curr) => curr + prev, 0)
            + Math.floor(remainingMinutesInRow / stepInMinutes * rowsHeights[row]);
    }

    const getTopPixelForRow = (idx: number) => getTopPixelForTime(openingHourInMinutesOfDay + idx*stepInMinutes);
    
    const rowsHeights = Array.apply(0, Array(Math.ceil(nrTimeRows))).map((_, row: number) => {
        if (drawMode === 'name-only') {
            return Math.max(
                effectiveRowHeight,
                columns.map(({name}) => schedulesInsideCell(name, row).length).reduce((prev, curr) => Math.max(prev, curr), 0) * nameOnlyHeight)
        }
        if (drawMode === 'block') {
            return Math.max(
                effectiveRowHeight,
                columns.map(({name}) => {
                    const shortestDuration = getShortestDuration(schedulesInsideCell(name, row))
                    return (minBlockHeight*stepInMinutes/shortestDuration)-gridGap
                }).reduce((prev, curr) => Math.max(prev, curr), 0) - gridGap
            )
        }
        return effectiveRowHeight
    });

    const getHeight = (minOfDay: number, minDuration: number) => {
        let row = getScheduleRow(minOfDay)
        let time = minOfDay
        let duration = Math.max(minDuration, minVisualDurationMinutes)
        let height = 0
        
        while(true) {
            let minNextStep = openingHourInMinutesOfDay+stepInMinutes*(row + 1)
            if (minNextStep >= time + duration) {
                height += (rowsHeights[row]+gridGap)*duration/stepInMinutes
                break
            } else {
                let durationInThisRow = minNextStep-time
                height += rowsHeights[row]*durationInThisRow/stepInMinutes + gridGap
                row++
                time=minNextStep
                duration-=durationInThisRow
            }
        }

        return height
    }

    const getMinutesOfDay = (height: number) => {
        let row = 0
        height -= gridGap
        while (height > rowsHeights[row] + gridGap) {
            height -= (rowsHeights[row] + gridGap)
            row++
        }
        
        const rowTime = row * stepInMinutes + openingHourInMinutesOfDay 
        const restRowPercentage = height / (rowsHeights[row] + gridGap)
        const res = rowTime + restRowPercentage*stepInMinutes
        
        return res
    }

    const totalHeight = rowsHeights.reduce((total, rowHeight) => total + rowHeight + gridGap, 0)

    const timesStrings = Array.apply(0, Array(Math.ceil(nrTimeRows)))
        .map((_, idx) => idx*stepInMinutes + openingHourInMinutesOfDay)
        .map(minute => `${("0" + Math.trunc(minute / 60)).slice(-2)}:${("0" + minute % 60).slice(-2)}`);

    const timeGridStyle: React.CSSProperties = {
        gridGap: `${gridGap}px`,
        gridTemplateColumns: `${timeColumnWidth}px`, 
        gridTemplateRows: `${headerHeight+gridGap/2}px ${rowsHeights.map(h => `${h}px`).join(' ')}`,
    };

    const gridStyle: React.CSSProperties = {
        gridGap: `${gridGap}px`,
        gridTemplateColumns: `repeat(${nrColumns}, ${columnsWidth}px)`, 
        gridTemplateRows: `${headerHeight}px ${rowsHeights.map(h => `${h}px`).join(' ')}`,
    };

    const scheduleContentStyle: React.CSSProperties = {
        fontSize: `${fontSize}px`,
    };

    const headerStyle = (idx: number): React.CSSProperties => ({
        gridColumn:1+idx, 
        gridRow: 1,
    });

    const timeStyle = (idx: number): React.CSSProperties => ({
        gridColumn: 1, 
        gridRow: idx+2,
        height: rowsHeights[idx],
    });

    const overlaps = useMemo(() => {
        const value: Record<string, string[]> = {}
        for (const schedule of schedules) { // TODO - deal with cascade
            if (schedule.ignoreOverlap) continue
            const scheduleOverlaps = schedules.filter(s => s.columnName === schedule.columnName && !s.ignoreOverlap && (
              (s.startMinutesOfDay >= schedule.startMinutesOfDay && s.startMinutesOfDay < schedule.endMinutesOfDay) ||
              (s.endMinutesOfDay > schedule.endMinutesOfDay && s.startMinutesOfDay < schedule.endMinutesOfDay) ||
              (s.startMinutesOfDay <= schedule.startMinutesOfDay && s.endMinutesOfDay >= schedule.endMinutesOfDay)
            )).map(s => s.id)
            const allScheduleOverlaps = [...new Set([...scheduleOverlaps, ...(value[schedule.id] ?? [])])]
            for (const id of allScheduleOverlaps) value[id] = allScheduleOverlaps
        }
        return value
    }, [schedules]) 

    const scheduleStyle = (schedule: CalendarSchedule): React.CSSProperties => {
        const column = columns.findIndex(({name}) => name === schedule.columnName);
        const start = Math.max(openingHourInMinutesOfDay - 20, schedule.startMinutesOfDay);
        const end = Math.min(closingHourInMinutesOfDay + 60, schedule.endMinutesOfDay);
        const durationMinutes = Math.max((end - start), minVisualDurationMinutes);

        const width = columnsWidth/(overlaps[schedule.id]?.length ?? 1)
        const overlapLeftCompensation = (overlaps[schedule.id]?.indexOf(schedule.id) ?? 0)*width

        switch(drawMode){
            default:
            case 'block':
                return {
                    ...blockStyle(schedule.color, schedule.opacity),
                    top: `${getHeight(openingHourInMinutesOfDay, start - openingHourInMinutesOfDay) + gridGap/2}px`, 
                    left: `${(columnsWidth+gridGap)*column + overlapLeftCompensation}px`,
                    width: `${width}px`, 
                    height: `${getHeight(start, durationMinutes) + 1}px`,
                };
            case 'name-only':
                const schedulesInSameCell = schedulesInsideCell(schedule.columnName, getScheduleRow(start));
                return {
                    ...nameOnlyStyle(schedule.color),
                    top: `${gridGap/2+getTopPixelForRow(getScheduleRow(start)) + schedulesInSameCell.findIndex(s => s === schedule) * nameOnlyHeight}px`,
                    left: `${(columnsWidth+gridGap)*column}px`,
                    width: `${columnsWidth}px`, 
                    height: '15px', 
                };
        }
    };

    const hrStyle = (row: number): React.CSSProperties => ({
        position: 'absolute',
        margin: 0,
        width: `${nrColumns*(columnsWidth+gridGap)-gridGap}px`,
        left: 0,
        top: `${getTopPixelForRow(row) + gridGap/2}px` 
    });

    const vrStyle = (column: number): React.CSSProperties => ({
        position: 'absolute',
        margin: 0,
        width: 0,
        borderLeft: 'solid 1px lightgrey',
        height: `${totalHeight + 80}px`,
        top: `-40px`, 
        left: `${(columnsWidth+gridGap)*column - gridGap/2}px`,
    });

    const getToolTipContent = (toolTipContent: React.ReactElement | string): React.ReactElement => (
        <div style={{fontSize: `${fontSize}px`}}>
            {toolTipContent}
        </div>
    );

    const onClickSchedule = (schedule: CalendarSchedule) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (longPressFiredRef.current) { longPressFiredRef.current = false; return; }
        if (!schedule.onClick) return
        event.stopPropagation()
        const minutes = getMinutesOfDay(event.clientY - (containerRef.current?.getBoundingClientRect().top ?? 0))
        schedule.onClick(minutes, event);
    }

    const onClickEmpty = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (longPressFiredRef.current) { longPressFiredRef.current = false; return; }
        if (!onClick) return
        event.stopPropagation()

        const minutes = getMinutesOfDay(event.clientY - (containerRef.current?.getBoundingClientRect().top ?? 0))
        const columnIdx = Math.floor((event.clientX - (containerRef.current?.getBoundingClientRect().left ?? 0)) / (columnsWidth + gridGap))

        if (minutes < openingHourInMinutesOfDay || minutes > closingHourInMinutesOfDay || columnIdx < 0 || columnIdx >= columns.length) {
            return
        }

        onClick(columns[columnIdx], minutes, event);
    }

    const onContextMenuEmpty = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!onContextMenuBackground) return
        event.preventDefault()
        event.stopPropagation()

        const minutes = getMinutesOfDay(event.clientY - (containerRef.current?.getBoundingClientRect().top ?? 0))
        const columnIdx = Math.floor((event.clientX - (containerRef.current?.getBoundingClientRect().left ?? 0)) / (columnsWidth + gridGap))

        if (minutes < openingHourInMinutesOfDay || minutes > closingHourInMinutesOfDay || columnIdx < 0 || columnIdx >= columns.length) {
            return
        }

        onContextMenuBackground(columns[columnIdx], minutes, event);
    }

    const onContextMenu = (schedule: CalendarSchedule) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!schedule.onContextMenu) return
        const minutes = getMinutesOfDay(event.clientY - (containerRef.current?.getBoundingClientRect().top ?? 0))
        schedule.onContextMenu(minutes, event);
    }

    const onMouseOver = (schedule: CalendarSchedule) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!schedule.onMouseOver) return
        const minutes = getMinutesOfDay(event.clientY - (containerRef.current?.getBoundingClientRect().top ?? 0))
        schedule.onMouseOver(minutes, event);
    }

    return (
        <div
            ref={outerContainerRef}
            onClick={onClickEmpty}
            onContextMenu={onContextMenuEmpty}
            onMouseMove={onMouseMoveContainer}
            onMouseLeave={onMouseLeaveContainer}
            onTouchStart={onTouchStartBackground}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            className={classnames('customDayCalendarContainer', className)}
            style={{...style, maxWidth: `min(100%, ${90 + (columnsWidth + gridGap) * columns.length}px)`}}
        >
            <IconButton
                onClick={() => setDrawMode(prev => prev === 'block' ? 'name-only' : 'block')}
                style={{position: 'relative', height: 0, width: 0, margin: 0, padding: 0, top: '10px', left: '10px'}}
            >
                {drawMode === 'block' ? <ViewHeadlineIcon/> : <ViewColumnIcon/>}
            </IconButton>
            {onSettingsClick && (
                <IconButton
                    onClick={e => { e.stopPropagation(); onSettingsClick(); }}
                    style={{position: 'relative', height: 0, width: 0, margin: 0, padding: 0, top: '10px', left: '34px'}}
                >
                    <SettingsIcon/>
                </IconButton>
            )}

            <div style={timeGridStyle} className="timeColumn">
                <div style={{ gridColumn: 1, gridRow: 1 }}/>
                {
                    timesStrings.map((time, idx) => {
                        const minuteOfDay = openingHourInMinutesOfDay + idx * stepInMinutes;
                        const isHour = minuteOfDay % 60 === 0;
                        return (
                            <span
                                key={idx}
                                style={timeStyle(idx)}
                                className={classnames('timeCell', isHour ? 'hourCell' : 'subHourCell')}
                            >
                                {time}
                            </span>
                        );
                    })
                }
            </div>
            
            <div style={gridStyle} className="grid">
            
                <div style={{position: 'relative'}} ref={containerRef}>
                    {
                        timesStrings.map((_, idx) => {
                            if (idx === 0) return null;
                            const minuteOfDay = openingHourInMinutesOfDay + idx * stepInMinutes;
                            const isSubHour = minuteOfDay % 60 !== 0;
                            return <hr key={`ts-${idx}`} style={hrStyle(idx)} className={isSubHour ? 'subHourLine' : 'hourLine'}/>;
                        })
                    }
                    {
                        columns.map((_, idx) => idx === 0 ? null : (<hr key={`c-${idx}`} style={vrStyle(idx)}/>))
                    }
                    {
                        schedules.map((s, idx) => (
                            <React.Fragment key={`schedule-wrapper-${idx}`}>
                                {!!s.toolTipContent && <ToolTip target={`schedule-${idx}`} toolTipContent={getToolTipContent(s.toolTipContent)} />}
                                <div style={scheduleStyle(s)} className="schedule" id={`schedule-${idx}`} onClick={onClickSchedule(s)} onContextMenu={onContextMenu(s)} onMouseMove={onMouseOver(s)} onTouchStart={onTouchStartSchedule(s)}>
                                    <p style={scheduleContentStyle} className="scheduleContent">
                                        {s.content}
                                    </p>
                                </div>
                            </React.Fragment>
                        ))
                    }
                </div>

                { 
                    columns.map((col, idx) => (
                        <React.Fragment key={`column-header-${col.name}`}>
                            <span 
                                id={col.name}
                                draggable
                                onDrop={e => {onChangeColumnOrder && onChangeColumnOrder(parseInt(e.dataTransfer.getData('text')), idx)}}
                                onDragStart={e => {e.dataTransfer.setData('text', idx.toString())}}
                                onDragOver={e => {e.preventDefault()}}
                                style={headerStyle(idx)} 
                                className={`calendarHeader ${col.highlighted ? 'calendarHeaderHighlight' : ''} ${col.lowlighted ? 'calendarHeaderLowlight' : ''}`}
                            >
                                {col.name}
                            </span>
                            {!!col.subtitle &&
                                <span style={headerStyle(idx)} className={`calendarHeaderSubtitle ${col.highlighted ? 'calendarHeaderHighlight' : ''} ${col.lowlighted ? 'calendarHeaderLowlight' : ''}`}>
                                    {col.subtitle}
                                </span>
                            }
                        </React.Fragment>
                    ))
                }
            </div>
            {mouseLabel && mousePos && (
                <div style={{
                    position: 'absolute',
                    left: mousePos.x,
                    top: mousePos.y,
                    pointerEvents: 'none',
                    zIndex: 10,
                    background: 'var(--bg-light)',
                    border: 'var(--border-thin) solid var(--appLigthGrey)',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: `${fontSize}px`,
                    whiteSpace: 'nowrap',
                }}>
                    {typeof mouseLabel === 'function' ? mouseLabel(mousePos.minutes) : mouseLabel}
                </div>
            )}
        </div>
    );
}

export default CustomDayCalendar;
